import * as React from 'react';
import { cn } from '../../lib/utils';

export interface SparklineProps {
  /** Data values — taken in order. */
  data: number[];
  /** Optional setpoint reference line. */
  setpoint?: number;
  /** Width in px. */
  width?: number;
  /** Height in px. */
  height?: number;
  /** Color override; default uses Phosphor for live data. */
  color?: 'phosphor' | 'primary' | 'foreground' | 'state-running' | 'alarm-major';
  /** Whether the line should pulse (for "live" data). */
  live?: boolean;
  /** Show a dot at the latest point. */
  showLatest?: boolean;
  className?: string;
  ariaLabel?: string;
}

/**
 * Tyndo Sparkline — pure SVG, no Recharts dependency.
 *
 * Inline trend-line for KPI cards, table cells, freshness widgets. The default
 * Phosphor color signals "live data"; pass `live={true}` for a phosphor pulse
 * on the latest point.
 */
export function Sparkline({
  data,
  setpoint,
  width = 80,
  height = 24,
  color = 'phosphor',
  live = false,
  showLatest = true,
  className,
  ariaLabel,
}: SparklineProps) {
  if (data.length === 0) {
    return (
      <span
        className={cn('inline-block text-muted-foreground/40', className)}
        style={{ width, height }}
        aria-label="No data"
      />
    );
  }

  const min = Math.min(...data);
  const max = Math.max(...data);
  const span = Math.max(1e-6, max - min);
  const stepX = data.length > 1 ? width / (data.length - 1) : 0;

  const points = data.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / span) * height;
    return { x, y };
  });

  const first = points[0];
  const path =
    points.length > 1 && first
      ? `M ${first.x},${first.y} ${points
          .slice(1)
          .map((p) => `L ${p.x},${p.y}`)
          .join(' ')}`
      : `M 0,${height / 2} L ${width},${height / 2}`;

  const stroke = `hsl(var(--${
    color === 'phosphor'
      ? 'accent-live'
      : color === 'primary'
        ? 'primary'
        : color === 'foreground'
          ? 'foreground'
          : color === 'state-running'
            ? 'state-running'
            : 'alarm-major'
  }))`;
  const fill = stroke;

  const setpointY =
    setpoint !== undefined ? height - ((setpoint - min) / span) * height : null;

  const last = points[points.length - 1];

  return (
    <svg
      role="img"
      aria-label={ariaLabel ?? 'Trend sparkline'}
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className={cn('inline-block overflow-visible', className)}
    >
      {setpointY !== null && (
        <line
          x1={0}
          y1={setpointY}
          x2={width}
          y2={setpointY}
          stroke="currentColor"
          strokeOpacity={0.25}
          strokeDasharray="2 2"
          strokeWidth={1}
        />
      )}
      <path
        d={path}
        fill="none"
        stroke={stroke}
        strokeWidth={1.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {showLatest && last && (
        <g>
          <circle cx={last.x} cy={last.y} r={2} fill={fill} />
          {live && (
            <circle
              cx={last.x}
              cy={last.y}
              r={4}
              fill={fill}
              opacity={0.4}
              className="tyndo-pulse"
            />
          )}
        </g>
      )}
    </svg>
  );
}
